import { useEffect, useRef } from 'react';
import { useCompanySettings } from '../../CompanySettingsContext';
import { subscribeMfaRequired } from '../../utils/auth/mfaRequiredSignal';

export const MFA_SIGNAL_RECHECK_MIN_MS = 3000;
export const MFA_RESUME_RECHECK_MIN_MS = 30000;

function policyKeyOf(floor, required) {
  const norm = (list) => [...new Set(list || [])].sort().join(',');
  return `${norm(floor)}|${norm(required)}`;
}

/**
 * KAN-529 — tells ProtectedRoute to re-ask the server whether this account
 * must finish 2FA (`get_my_mfa_requirement`) at the moments the answer can
 * change mid-session. A plain RLS denial returns `200 []`, so list screens
 * never see an error to react to — without these triggers a worker whose
 * role just became 2FA-required would sit on empty screens.
 *
 * Triggers (no new polling loop):
 *   1. a request came back MFA_REQUIRED (mfaRequiredSignal)        — ≥3s apart
 *   2. app resume / window focus, same 30s throttle as the existing
 *      CompanySettingsContext resume refresh                         — ≥30s apart
 *   3. the company's 2FA policy (floor ∪ company setting) changed in
 *      CompanySettingsContext (which re-reads it on resume)          — immediate
 *
 * `paused` (already on the 2FA challenge / enrollment screen) skips the
 * refusal-signal trigger: those screens' own reads come back MFA_REQUIRED by
 * design, and re-checking every 3s there changes nothing.
 *
 * @param {{ sessionUserId: string|null|undefined, onRecheck: () => void, paused?: boolean }} args
 */
export default function useMfaServerRecheck({ sessionUserId, onRecheck, paused = false }) {
  const onRecheckRef = useRef(onRecheck);
  onRecheckRef.current = onRecheck;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  // 1. MFA_REQUIRED refusal from any request.
  const lastSignalRef = useRef(0);
  useEffect(() => subscribeMfaRequired(() => {
    if (pausedRef.current) return;
    const now = Date.now();
    if (now - lastSignalRef.current < MFA_SIGNAL_RECHECK_MIN_MS) return;
    lastSignalRef.current = now;
    onRecheckRef.current?.();
  }), []);

  // 2. Resume / focus.
  const lastResumeRef = useRef(0);
  useEffect(() => {
    if (!sessionUserId || typeof document === 'undefined') return undefined;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastResumeRef.current < MFA_RESUME_RECHECK_MIN_MS) return;
      lastResumeRef.current = now;
      onRecheckRef.current?.();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [sessionUserId]);

  // 3. Company 2FA policy changed (first value is the baseline, not a change).
  const { mfaFloorRoles, mfaRequiredRoles } = useCompanySettings();
  const policyKey = policyKeyOf(mfaFloorRoles, mfaRequiredRoles);
  const policyKeyRef = useRef(null);
  useEffect(() => {
    if (policyKeyRef.current === null) { policyKeyRef.current = policyKey; return; }
    if (policyKeyRef.current === policyKey) return;
    policyKeyRef.current = policyKey;
    if (sessionUserId) onRecheckRef.current?.();
  }, [policyKey, sessionUserId]);
}
